import type { BaseTheme } from "common/BaseTheme";
import { asString, loadedDefinedMapWithDefault } from "common/components/component-helpers";
import { AppKind, getAppKindFromFeatures } from "common/components/SerializedApp";
import type { AppFeatures, IconImage } from "common/components/SerializedApp";
import type {
    AppUserAuthenticator,
    AppUserChangedCallback,
    ForceValueProducer,
    ResponseStatus,
} from "common/components/types";
import { SubscribingReactUnpureComponent } from "common/components/uis";
import type { EminenceFlags } from "common/Database/eminence";
import { logError, logInfo } from "common/debug-print";
import type { AppLoginTokenContainer, RegisterForPushNotificationsBody } from "common/firebase-function-types";
import { memoizeFunction } from "common/memoize";
import { DeviceFormFactor } from "common/render/form-factor";
import { isResponseOK, safeURL } from "common/Support";
import { withBuilderEminenceFlags } from "hooks/use-builder-eminence-flags";
import { withPlayerEminenceFlags } from "hooks/use-player-eminence-flags";
import MobileDetect from "mobile-detect";
import { createPortal } from "react-dom";
import { isOSThemeDark, watchOSThemeDark } from "sharedUI/lib/color-theme-utils";
import { TailwindThemeProvider } from "sharedUI/lib/tailwind-theme-provider";
import {
    getAddToHomeKey,
    getEmailKey,
    getLoginTokenKey,
    getPasswordForEmailKey,
    getPasswordKey,
    getUsernameKey,
    localStorageGetItem,
    localStorageRemoveItem,
    localStorageSetItem,
} from "support/local-storage";
import InAppAdView from "webapp/chrome/views/in-app-ad/in-app-ad-view";
import { APP_MODAL_ROOT } from "webapp/lib/constants";
import type { InYourFaceBrandingProps } from "webapp/lib/in-your-face-branding";
import { initInYourFaceBranding } from "webapp/lib/in-your-face-branding";

import deploymentVersion from "../../../../deployment-version.json";
import { browserCertainlyIsOniOS, browserIsAndroidChrome, browserIsSafari } from "../../../../lib/browser-detect";
import { getAppFacilities } from "../../../lib/app-renderer";
import { standalone } from "../../../lib/device";
import type { ThemePlatform } from "../../../lib/theme/theme";
import { getRuntimeThemeForPlatform, getWireAppThemeForPlatform } from "../../../lib/theme/theme";
import AddToHome from "../../views/add-to-home/add-to-home";
import App from "../../views/app/app";
import HomeBar from "../../views/home-bar/home-bar";
import StatusBar from "../../views/status-bar/status-bar";
import FontScaleProvider from "../font-scale-provider/font-scale-provider";
import {
    CredentialsProvider,
    clearAppPasswordLocalStorage,
    getAppLoginTokenExpirationTimestamp,
    shouldSaveLogin,
    tryRegisterOnPushTokenLoadCallback,
    tryUnregisterOnPushTokenLoadCallback,
} from "./app-container-lib";
import type { AppCredentials, UsernameProxy } from "./app-container-types";

interface Props {
    title: string | undefined;
    iconImage: IconImage | undefined;
    theme: BaseTheme | undefined;
    useFrame: boolean;
    forceTheme?: ThemePlatform;
    isSplash?: boolean;
    appID: string | undefined;
    deviceFormFactor: DeviceFormFactor;
    isBuilder: boolean;
    showBranding: boolean;
    showingApp: boolean;
    appFeatures: AppFeatures;
    eminenceFlags: EminenceFlags;
    authenticator: AppUserAuthenticator | undefined;
}

interface State {
    showAddToHome: boolean;
    isDarkMode: boolean;

    brandingProps: InYourFaceBrandingProps | undefined;
    reportBrandingResult: ((converted: boolean) => void) | undefined;
}

let hasShownAddToHomeOnce: boolean = false;

// This is so that we can share authentication context
// across many apps in the builder
let credStore = {
    username: "",
    email: "",
    emailPassword: "",
    canChangeEmail: true,
    canChangeUsername: true,
    isAuthenticated: false,
};

const memoizeTheme = memoizeFunction(
    "memoizeTheme",
    (
        isWireApp: boolean,
        theme: BaseTheme | undefined,
        platform: ThemePlatform,
        deviceFormFactor: DeviceFormFactor,
        isDarkMode: boolean
    ) => {
        if (isWireApp) {
            return getWireAppThemeForPlatform(theme, platform, false);
        } else {
            return getRuntimeThemeForPlatform(theme, platform, deviceFormFactor, isDarkMode);
        }
    }
);

export class AppContainer extends SubscribingReactUnpureComponent<Props, State> {
    private appUserPushCallbacks: { [appUserID: string]: (token: string) => Promise<void> } = {};
    private verifiedEmailChangeCallback?: AppUserChangedCallback;
    private urlSanitizationObserver: MutationObserver | undefined;

    private proxy: UsernameProxy | undefined;

    public expandState() {
        return {
            showAddToHome: false,
            isDarkMode: isOSThemeDark(),
            brandingProps: undefined,
            reportBrandingResult: undefined,
        };
    }

    constructor(props: Props) {
        super(props);

        const appID = props.appID;

        if (props.isBuilder === true) {
            this.appCredentials = { ...this.appCredentials, isAuthenticated: true };
        }

        if (appID !== undefined) {
            const username = localStorageGetItem(getUsernameKey(appID));
            if (username !== undefined) {
                this.appCredentials = { ...this.appCredentials, username };
            }

            const email = localStorageGetItem(getEmailKey(appID));
            if (email !== undefined) {
                this.appCredentials = { ...this.appCredentials, email };
            }

            const emailPassword = localStorageGetItem(getPasswordForEmailKey(appID));
            if (emailPassword !== undefined) {
                this.appCredentials = { ...this.appCredentials, emailPassword };
            }

            const password = localStorageGetItem(getPasswordKey(appID));
            if (password !== undefined) {
                this.appCredentials = { ...this.appCredentials, password };
            }

            this.verifiedEmailChangeCallback = async (appUserID, realEmail, _virtualEmail, loginToken) => {
                if (appUserID === undefined) return;
                logInfo(`appUserID is ${appUserID}`);

                if (this.props.isBuilder) {
                    this.appCredentials = { ...this.appCredentials, email: realEmail ?? "" };
                } else if (
                    // There is a race condition between this and updateCredentials:
                    // someone (either us or them) might be attempting to assert a
                    // relatively stale login token over the other.
                    loginToken !== undefined &&
                    (this.appCredentials.loginToken === undefined ||
                        getAppLoginTokenExpirationTimestamp(this.appCredentials.loginToken) <
                            getAppLoginTokenExpirationTimestamp(loginToken))
                ) {
                    this.appCredentials = { ...this.appCredentials, loginToken };
                    localStorageSetItem(getLoginTokenKey(appID), JSON.stringify(loginToken));
                }

                if (this.appUserPushCallbacks[appUserID] !== undefined) return;
                const handlePushNotificationToken = async (token: string) => {
                    const body: RegisterForPushNotificationsBody = { appID, appUserID, registrationToken: token };
                    const response = await getAppFacilities().callAuthCloudFunction(
                        "registerForPushNotifications",
                        body,
                        {}
                    );
                    if (!isResponseOK(response)) {
                        const reason = response === undefined ? "Network error" : response.statusText;
                        logError(`Could not register ${appUserID} in ${appID} for push notifications: ${reason}`);
                    }
                };
                this.appUserPushCallbacks[appUserID] = handlePushNotificationToken;
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                tryRegisterOnPushTokenLoadCallback(handlePushNotificationToken);
            };

            this.props.authenticator?.addCallback(this.verifiedEmailChangeCallback);
        }
    }

    // FIXME: This is not the most appropriate way to do this.
    // We really should be more vigilant about sanitizing URLs as they render in apps
    // _before_ they hit the DOM. What's currently thwarting us is that we allow HTML
    // in Markdown components, and there's no solid way to fix _those_ URLs.
    private hookURLSanitizationObserver = (element: HTMLElement) => {
        // element === null when the component unmounts.
        // That means it's time to disconnect.
        if (element === null) {
            if (this.urlSanitizationObserver !== undefined) {
                this.urlSanitizationObserver.disconnect();
                this.urlSanitizationObserver = undefined;
            }
            return;
        }

        const fixupHrefs = (target: Element) => {
            const origHREF = target.getAttribute("href");
            if (origHREF === null) return;

            const fixedHREF = safeURL(origHREF);

            if (fixedHREF === undefined) {
                target.removeAttribute("href");
            } else if (fixedHREF !== origHREF) {
                target.setAttribute("href", fixedHREF);
            }
        };

        this.urlSanitizationObserver = new MutationObserver(mutations => {
            for (const mutation of mutations) {
                const { target, type, attributeName, addedNodes } = mutation;

                if (type === "attributes" && attributeName === "href" && target instanceof Element) {
                    fixupHrefs(target);
                } else if (type === "childList") {
                    addedNodes.forEach(node => {
                        if (!(node instanceof Element)) return;
                        fixupHrefs(node);

                        const anchorNodes = node.getElementsByTagName("a");
                        // Yuck, yes, but this is all the types allow us to do.
                        for (let i = 0; i < anchorNodes.length; i++) {
                            fixupHrefs(anchorNodes[i]);
                        }
                    });
                }
            }
        });

        this.urlSanitizationObserver.observe(element, {
            subtree: true,
            childList: true,
            attributes: true,
            attributeFilter: ["href"],
        });
    };

    private setAuthenticated(isAuthenticated: boolean) {
        this.appCredentials = {
            ...this.appCredentials,
            isAuthenticated,
            canChangeEmail: !isAuthenticated,
        };

        credStore = this.appCredentials;
        this.forceUpdate();
    }

    private updateCredentials(
        email: string | undefined,
        emailPassword: string | undefined,
        username: string | undefined,
        loginToken: AppLoginTokenContainer | undefined,
        forceUpdate: boolean
    ) {
        const newCredentials = { ...this.appCredentials };
        if (email !== undefined) {
            newCredentials.email = email;
        }
        if (emailPassword !== undefined) {
            newCredentials.emailPassword = emailPassword;
        }
        if (username !== undefined) {
            newCredentials.username = username;
        }
        if (
            // There is a race condition between this and verifiedEmailChangeCallback:
            // someone (either us or them) might be attempting to assert a
            // relatively stale login token over the other.
            loginToken !== undefined &&
            (this.appCredentials.loginToken === undefined ||
                getAppLoginTokenExpirationTimestamp(this.appCredentials.loginToken) <
                    getAppLoginTokenExpirationTimestamp(loginToken))
        ) {
            newCredentials.loginToken = loginToken;
        }
        this.appCredentials = newCredentials;

        if (this.props.appID !== undefined) {
            if (shouldSaveLogin(this.props.appID, this.props.appFeatures)) {
                localStorageSetItem(getUsernameKey(this.props.appID), this.appCredentials.username);
                localStorageSetItem(getEmailKey(this.props.appID), this.appCredentials.email);
                localStorageSetItem(getPasswordForEmailKey(this.props.appID), this.appCredentials.emailPassword);

                const loginTokenKey = getLoginTokenKey(this.props.appID);
                if (this.appCredentials.loginToken === undefined) {
                    localStorageRemoveItem(loginTokenKey);
                } else {
                    localStorageSetItem(
                        getLoginTokenKey(this.props.appID),
                        JSON.stringify(this.appCredentials.loginToken)
                    );
                }
            } else {
                clearAppPasswordLocalStorage(this.props.appID);
            }
        }

        credStore = this.appCredentials;

        if (forceUpdate) {
            this.forceUpdate();
        }
    }

    private sendPin = async (email: string): Promise<ResponseStatus> => {
        const { authenticator } = this.props;
        if (authenticator === undefined) return "Offline";
        const result = await authenticator.sendPinForEmail(email, deploymentVersion.deploymentVersion);
        return result;
    };

    private passwordForPin = async (
        email: string,
        pin: string,
        userAgreed: boolean
    ): Promise<{ password: string; loginToken: AppLoginTokenContainer | undefined } | undefined> => {
        const { authenticator } = this.props;
        if (authenticator === undefined) return undefined;

        const result = await authenticator.getPasswordForEmailPin(email, pin, userAgreed);
        if (result !== undefined) {
            this.updateCredentials(email, result.password, undefined, result.loginToken, false);
            this.setAuthenticated(true);
        }
        return result;
    };

    private emailAndPasswordForOAuth2Token = async (
        authToken: string,
        userAgreed: boolean
    ): Promise<
        { email: string; password: string | undefined; loginToken: AppLoginTokenContainer | undefined } | undefined
    > => {
        const { authenticator } = this.props;
        if (authenticator === undefined) return undefined;

        const result = await authenticator.getPasswordForOAuth2Token(authToken, userAgreed);
        if (result !== undefined) {
            const { email, password, displayName, appLoginToken } = result;
            if (password !== undefined) {
                this.updateCredentials(email, password, displayName, appLoginToken, false);
                this.setAuthenticated(true);
            }
            return { email, password, loginToken: appLoginToken };
        } else {
            return undefined;
        }
    };

    private authenticate = async (
        email: string,
        password: string,
        authToken: AppLoginTokenContainer | undefined
    ): Promise<boolean> => {
        const { authenticator } = this.props;
        if (authenticator === undefined) return false;

        let result: boolean;
        if (authToken !== undefined) {
            result = await authenticator.authorizeForAppWithLoginToken(authToken, email, password);
        } else {
            result = await authenticator.authorizeForAppWithEmail(email, password);
        }
        if (result) {
            this.updateCredentials(email, password, undefined, authToken, false);
            this.setAuthenticated(true);
        }
        return result;
    };

    private setUsername = async (username: string): Promise<boolean> => {
        const proxy = this.proxy;

        if (proxy !== undefined) {
            this.updateCredentials(undefined, undefined, username, undefined, true);
            proxy.setUsername(username);
        } else {
            this.updateCredentials(undefined, undefined, username, undefined, true);
        }
        return true;
    };

    private getAuthenticator = () => {
        return this.props.authenticator;
    };

    private setUsernameProxy = (proxy: UsernameProxy) => {
        const usernamePV = proxy.getUsername().current;
        const username = loadedDefinedMapWithDefault(usernamePV, this.appCredentials.username, asString);
        if (username !== this.appCredentials.username) {
            this.updateCredentials(undefined, undefined, username, undefined, true);
        }

        if (this.proxy === undefined) {
            this.forceUpdate();
        }
        this.proxy = proxy;
    };

    private appCredentials: AppCredentials = {
        ...credStore,
        password: "",
        loginToken: undefined,
        sendPin: this.sendPin,
        passwordForPin: this.passwordForPin,
        emailAndPasswordForOAuth2Token: this.emailAndPasswordForOAuth2Token,
        authenticate: this.authenticate,
        setUsername: this.setUsername,
        getAuthenticator: this.getAuthenticator,
        setUsernameProxy: this.setUsernameProxy,
    };

    private get showInYourFaceBranding(): boolean {
        const { isBuilder, showBranding, showingApp, eminenceFlags } = this.props;
        const allowedByEminence = eminenceFlags.inYourFaceBranding;
        return !isBuilder && showBranding && showingApp && allowedByEminence;
    }

    private readonly inYourFaceBrandingCallback = (
        props: InYourFaceBrandingProps,
        reportResult: (converted: boolean) => void
    ) => {
        this.setState({ brandingProps: props, reportBrandingResult: reportResult });
    };

    private initInYourFaceBranding(): void {
        const { appID, appFeatures } = this.props;
        if (this.showInYourFaceBranding && appID !== undefined) {
            // This call is idempotent
            initInYourFaceBranding(appID, getAppKindFromFeatures(appFeatures), this.inYourFaceBrandingCallback);
        }
    }

    private onClose = () => {
        localStorageSetItem(getAddToHomeKey(this.props.appID), "seen");
        this.setState({ showAddToHome: false });
    };

    public componentDidUpdate(): void {
        this.initInYourFaceBranding();
    }

    public componentDidMount() {
        if (
            !standalone &&
            browserCertainlyIsOniOS &&
            browserIsSafari &&
            getAppKindFromFeatures(this.props.appFeatures) === AppKind.App &&
            !hasShownAddToHomeOnce &&
            localStorageGetItem(getAddToHomeKey(this.props.appID)) !== "seen"
        ) {
            hasShownAddToHomeOnce = true;
            setTimeout(() => {
                this.setState({ showAddToHome: true });
            }, 200);
        }

        watchOSThemeDark(e => {
            if (this.state.isDarkMode === e) return;
            if (browserIsAndroidChrome) {
                window.location.reload();
            } else {
                this.setState({ isDarkMode: e });
            }
        });

        this.initInYourFaceBranding();
    }

    public componentWillUnmount() {
        if (this.verifiedEmailChangeCallback !== undefined) {
            this.props.authenticator?.removeCallback(this.verifiedEmailChangeCallback);
        }
        for (const appUserIDKey in this.appUserPushCallbacks) {
            if (this.appUserPushCallbacks[appUserIDKey] === undefined) continue;
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            tryUnregisterOnPushTokenLoadCallback(this.appUserPushCallbacks[appUserIDKey]);
        }
    }

    public recompute(force: ForceValueProducer): React.ReactNode {
        const { title, iconImage, theme, useFrame, isSplash, deviceFormFactor, appFeatures } = this.props;
        const { isDarkMode, showAddToHome, brandingProps, reportBrandingResult } = this.state;
        const proxy = this.proxy;
        let platform: ThemePlatform | undefined = this.props.forceTheme;
        if (platform === undefined) {
            if (new MobileDetect(window.navigator.userAgent).is("AndroidOS")) {
                platform = "Android";
            } else {
                platform = "iOS";
            }
        }
        const appKind = getAppKindFromFeatures(appFeatures);
        const isWireApp = appKind === AppKind.Page;
        const themeForPlatform = memoizeTheme(isWireApp, theme, platform, deviceFormFactor, isDarkMode);
        let statusBar = null;
        let homeBar = null;

        if (useFrame && deviceFormFactor !== DeviceFormFactor.Desktop) {
            statusBar = <StatusBar isSplash={isSplash === true} />;
            homeBar = <HomeBar isSplash={isSplash === true} />;
        }

        let overlay: React.ReactNode;
        if (showAddToHome) {
            if (showAddToHome === true && title !== undefined && iconImage !== undefined && appKind !== undefined) {
                overlay = (
                    <AddToHome title={title} iconImage={iconImage} onCloseTapped={this.onClose} appKind={appKind} />
                );
            }
        } else if (brandingProps !== undefined && this.showInYourFaceBranding && appKind !== undefined) {
            const adView = (
                <InAppAdView
                    {...brandingProps}
                    onClick={confirm => {
                        this.setState({ brandingProps: undefined, reportBrandingResult: undefined });
                        if (reportBrandingResult !== undefined) {
                            reportBrandingResult(confirm);
                        }
                    }}
                    appKind={appKind}
                />
            );
            const target = document.getElementById(APP_MODAL_ROOT);
            if (target !== null) {
                overlay = createPortal(adView, target);
            }
        }

        // The types for `ref` are wrong. It doesn't _have_ to be & string.
        const fixedRef = this.hookURLSanitizationObserver as ((element: HTMLElement) => void) & string;

        let creds = this.appCredentials;
        const username = proxy?.getUsername();
        if (username !== undefined) {
            const userString = loadedDefinedMapWithDefault(force(username), creds.username, asString);
            const canChangeUsername = proxy?.canSetUsername() ?? creds.canChangeUsername;
            creds = {
                ...creds,
                username: userString,
                canChangeUsername,
            };
        }

        return (
            <CredentialsProvider value={creds}>
                <TailwindThemeProvider theme={themeForPlatform}>
                    <FontScaleProvider>
                        <App className={useFrame ? "builder" : "player"} appKind={appKind} refCallback={fixedRef}>
                            {statusBar}
                            {this.props.children}
                            {homeBar}
                            {overlay}
                        </App>
                    </FontScaleProvider>
                </TailwindThemeProvider>
            </CredentialsProvider>
        );
    }
}

export default withPlayerEminenceFlags(AppContainer);

export const BuilderAppContainer = withBuilderEminenceFlags(AppContainer);
